import { useState, useEffect, useCallback, useRef } from "react";
import { LightPageLayout } from "../components/Layout";
import { API, useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Heart, MessageCircle, Pin, Flag, Trash2, Image, Film, BarChart3,
  Send, X, MoreHorizontal, ChevronDown, ChevronUp, Link2, Loader2,
  Users, GraduationCap, Briefcase, UserCheck, Globe
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "all", label: "All Posts", icon: Globe },
  { value: "prospective", label: "Prospective", icon: Users },
  { value: "participant", label: "Participants", icon: UserCheck },
  { value: "mentor", label: "Mentors", icon: Briefcase },
  { value: "alumni", label: "Alumni", icon: GraduationCap },
];

const CATEGORY_COLORS = {
  prospective: "bg-blue-100 text-blue-700 border-blue-200",
  participant: "bg-emerald-100 text-emerald-700 border-emerald-200",
  mentor: "bg-purple-100 text-purple-700 border-purple-200",
  alumni: "bg-amber-100 text-amber-700 border-amber-200",
};

const ROLE_LABELS = {
  participant: "Participant",
  instructor: "Instructor",
  mentor: "Mentor",
  admin: "Admin",
};

// ============ POST COMPOSER ============
const PostComposer = ({ onPostCreated }) => {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [media, setMedia] = useState([]);
  const [embedUrl, setEmbedUrl] = useState("");
  const [showEmbedInput, setShowEmbedInput] = useState(false);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [category, setCategory] = useState("");
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files.slice(0, 5)) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${API}/feed/upload`, {
          method: "POST", credentials: "include", body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setMedia(prev => [...prev, { type: data.type, url: data.url }]);
        } else { toast.error(`Failed to upload ${file.name}`); }
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addEmbed = () => {
    if (!embedUrl.trim()) return;
    const url = embedUrl.trim();
    let embedData = { type: "embed", url, embed_url: url };
    
    // YouTube detection
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    if (ytMatch) {
      embedData.embed_url = `https://www.youtube.com/embed/${ytMatch[1]}`;
      embedData.thumbnail = `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
    }
    
    // Giphy detection
    if (url.includes("giphy.com") || url.match(/\.gif$/i)) {
      embedData.type = "image";
    }
    
    setMedia(prev => [...prev, embedData]);
    setEmbedUrl("");
    setShowEmbedInput(false);
  };

  const removeMedia = (index) => setMedia(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (!content.trim() && media.length === 0 && !showPollCreator) return;
    setPosting(true);
    
    const postData = { content: content.trim(), media, category: category || undefined };
    if (showPollCreator && pollQuestion.trim()) {
      const validOptions = pollOptions.filter(o => o.trim());
      if (validOptions.length >= 2) {
        postData.poll = { question: pollQuestion.trim(), options: validOptions };
      }
    }
    
    try {
      const res = await fetch(`${API}/feed/posts`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(postData),
      });
      if (res.ok) {
        setContent(""); setMedia([]); setCategory("");
        setShowPollCreator(false); setPollQuestion(""); setPollOptions(["", ""]);
        toast.success("Posted!");
        onPostCreated?.();
      } else { toast.error("Failed to create post"); }
    } catch { toast.error("Error creating post"); }
    setPosting(false);
  };

  return (
    <Card className="card-light" data-testid="post-composer">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#0F1B3D] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {user?.name?.charAt(0) || "?"}
          </div>
          <div className="flex-1">
            <textarea
              value={content} onChange={(e) => setContent(e.target.value)}
              placeholder="Share something with the community..."
              className="w-full min-h-[80px] p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:border-[#FFD507] focus:ring-1 focus:ring-[#FFD507]/30"
              data-testid="post-content-input"
            />
          </div>
        </div>

        {/* Media Previews */}
        {media.length > 0 && (
          <div className="flex flex-wrap gap-2 pl-13">
            {media.map((m, i) => (
              <div key={i} className="relative group">
                {m.type === "image" || m.type === "embed" ? (
                  <img src={m.thumbnail || m.url} alt="" className="w-20 h-20 object-cover rounded-lg border" />
                ) : (
                  <div className="w-20 h-20 bg-slate-100 rounded-lg border flex items-center justify-center">
                    <Film className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <button onClick={() => removeMedia(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Embed URL Input */}
        {showEmbedInput && (
          <div className="flex gap-2 pl-13">
            <Input value={embedUrl} onChange={(e) => setEmbedUrl(e.target.value)} placeholder="Paste YouTube, Giphy, or image URL..." className="input-base text-sm" data-testid="embed-url-input" onKeyDown={(e) => e.key === "Enter" && addEmbed()} />
            <Button onClick={addEmbed} size="sm" className="btn-primary"><Link2 className="w-4 h-4" /></Button>
            <Button onClick={() => setShowEmbedInput(false)} size="sm" variant="ghost"><X className="w-4 h-4" /></Button>
          </div>
        )}

        {/* Poll Creator */}
        {showPollCreator && (
          <div className="pl-13 space-y-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
            <Input value={pollQuestion} onChange={(e) => setPollQuestion(e.target.value)} placeholder="Poll question..." className="input-base text-sm font-medium" data-testid="poll-question-input" />
            {pollOptions.map((opt, i) => (
              <div key={i} className="flex gap-2">
                <Input value={opt} onChange={(e) => { const n = [...pollOptions]; n[i] = e.target.value; setPollOptions(n); }} placeholder={`Option ${i + 1}`} className="input-base text-sm" />
                {pollOptions.length > 2 && (
                  <button onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                )}
              </div>
            ))}
            {pollOptions.length < 6 && (
              <button onClick={() => setPollOptions([...pollOptions, ""])} className="text-xs text-[#1E3A8A] font-medium hover:underline">+ Add option</button>
            )}
          </div>
        )}

        {/* Category Override */}
        <div className="flex items-center gap-2 pl-13 flex-wrap">
          <span className="text-xs text-slate-400">Filter by:</span>
          {CATEGORIES.slice(1).map(cat => (
            <button key={cat.value} onClick={() => setCategory(category === cat.value ? "" : cat.value)}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${category === cat.value ? CATEGORY_COLORS[cat.value] : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}
            >{cat.label}</button>
          ))}
          {category && <span className="text-[10px] text-slate-400">(override)</span>}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pl-13">
          <div className="flex items-center gap-1">
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple accept="image/*,video/*,.gif" className="hidden" />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500" title="Upload photo/video" data-testid="upload-media-btn">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Image className="w-5 h-5" />}
            </button>
            <button onClick={() => setShowEmbedInput(!showEmbedInput)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500" title="Embed URL" data-testid="embed-url-btn">
              <Link2 className="w-5 h-5" />
            </button>
            <button onClick={() => setShowPollCreator(!showPollCreator)} className={`p-2 rounded-lg hover:bg-slate-100 transition-colors ${showPollCreator ? 'text-[#1E3A8A]' : 'text-slate-500'}`} title="Create poll" data-testid="create-poll-btn">
              <BarChart3 className="w-5 h-5" />
            </button>
          </div>
          <Button onClick={handleSubmit} disabled={posting || (!content.trim() && media.length === 0 && !showPollCreator)} className="btn-primary px-5" data-testid="submit-post-btn">
            {posting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Post
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ POLL DISPLAY ============
const PollDisplay = ({ poll, postId, onVote }) => {
  const { user } = useAuth();
  const totalVotes = poll.options.reduce((sum, o) => sum + (o.votes?.length || 0), 0);
  const myVote = poll.options.findIndex(o => o.votes?.includes(user?.user_id));

  return (
    <div className="mt-3 p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid="poll-display">
      <p className="font-medium text-sm text-slate-800 mb-3">{poll.question}</p>
      <div className="space-y-2">
        {poll.options.map((opt, i) => {
          const votes = opt.votes?.length || 0;
          const pct = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
          const isMyVote = i === myVote;
          return (
            <button key={i} onClick={() => onVote(postId, i)} className={`w-full text-left relative rounded-lg border overflow-hidden transition-colors ${isMyVote ? 'border-[#FFD507] bg-[#FFD507]/5' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-testid={`poll-option-${i}`}>
              <div className="absolute inset-0 bg-[#FFD507]/10 rounded-lg" style={{ width: `${pct}%` }} />
              <div className="relative flex items-center justify-between px-3.5 py-2.5">
                <span className="text-sm text-slate-700">{opt.text}</span>
                <span className="text-xs font-medium text-slate-500">{pct}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-2">{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</p>
    </div>
  );
};

// ============ MEDIA DISPLAY ============
const MediaDisplay = ({ media }) => {
  if (!media || media.length === 0) return null;
  return (
    <div className={`mt-3 ${media.length === 1 ? '' : 'grid grid-cols-2 gap-2'}`}>
      {media.map((m, i) => {
        if (m.type === "video") {
          return <video key={i} src={m.url} controls className="w-full rounded-xl max-h-96 bg-black" data-testid={`media-video-${i}`} />;
        }
        if (m.embed_url?.includes("youtube.com/embed")) {
          return <iframe key={i} src={m.embed_url} title="Video" className="w-full aspect-video rounded-xl" allowFullScreen data-testid={`media-embed-${i}`} />;
        }
        return <img key={i} src={m.url || m.embed_url} alt="" className="w-full rounded-xl max-h-96 object-cover cursor-pointer hover:opacity-95 transition-opacity" data-testid={`media-image-${i}`} />;
      })}
    </div>
  );
};

// ============ COMMENT SECTION ============
const CommentSection = ({ postId, comments, onCommentAdded, onCommentDeleted }) => {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() || posting) return;
    setPosting(true);
    try {
      const res = await fetch(`${API}/feed/posts/${postId}/comments`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: content.trim() }),
      });
      if (res.ok) { setContent(""); onCommentAdded?.(); }
    } catch { toast.error("Failed to add comment"); }
    setPosting(false);
  };

  const handleDelete = async (commentId) => {
    try {
      const res = await fetch(`${API}/feed/comments/${commentId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) onCommentDeleted?.();
    } catch { toast.error("Failed to delete"); }
  };

  const displayComments = expanded ? comments : comments.slice(0, 2);

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      {comments.length > 2 && !expanded && (
        <button onClick={() => setExpanded(true)} className="text-xs text-[#1E3A8A] font-medium mb-2 hover:underline" data-testid="expand-comments">
          View all {comments.length} comments
        </button>
      )}
      <div className="space-y-2.5">
        {displayComments.map(c => (
          <div key={c.comment_id} className="flex items-start gap-2 group" data-testid={`comment-${c.comment_id}`}>
            <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 flex-shrink-0">
              {c.author_name?.charAt(0) || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="bg-slate-50 rounded-xl px-3 py-2">
                <span className="text-xs font-semibold text-slate-700">{c.author_name}</span>
                {c.author_role && <span className="text-[10px] text-slate-400 ml-1.5">{ROLE_LABELS[c.author_role] || c.author_role}</span>}
                <p className="text-sm text-slate-600 mt-0.5">{c.content}</p>
              </div>
              <div className="flex items-center gap-3 mt-0.5 px-1">
                <span className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleDateString()}</span>
                {(c.author_id === user?.user_id || user?.role === "admin") && (
                  <button onClick={() => handleDelete(c.comment_id)} className="text-[10px] text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">Delete</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2 mt-3">
        <Input value={content} onChange={(e) => setContent(e.target.value)} placeholder="Write a comment..." className="input-base text-sm rounded-xl" data-testid={`comment-input-${postId}`} />
        <Button type="submit" disabled={!content.trim() || posting} size="sm" className="btn-primary px-3 rounded-xl" data-testid={`comment-submit-${postId}`}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      </form>
    </div>
  );
};

// ============ FEED POST CARD ============
const FeedPostCard = ({ post, onRefresh, isAdmin }) => {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);

  const fetchComments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/feed/posts/${post.post_id}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch {}
  }, [post.post_id]);

  const handleLike = async () => {
    try {
      const res = await fetch(`${API}/feed/posts/${post.post_id}/like`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
        setLikeCount(prev => data.liked ? prev + 1 : prev - 1);
      }
    } catch {}
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      const res = await fetch(`${API}/feed/posts/${post.post_id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Post deleted"); onRefresh?.(); }
    } catch { toast.error("Failed to delete"); }
  };

  const handlePin = async () => {
    try {
      const res = await fetch(`${API}/feed/posts/${post.post_id}/pin`, { method: "PUT", credentials: "include" });
      if (res.ok) { onRefresh?.(); }
    } catch {}
  };

  const handleFlag = async () => {
    const reason = window.prompt("Why are you reporting this post?", "inappropriate");
    if (!reason) return;
    try {
      const res = await fetch(`${API}/feed/posts/${post.post_id}/flag`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      if (res.ok) toast.success("Post reported. An admin will review it.");
      else { const err = await res.json(); toast.error(err.detail || "Already reported"); }
    } catch {}
  };

  const handleDismissFlags = async () => {
    try {
      const res = await fetch(`${API}/feed/posts/${post.post_id}/dismiss-flags`, { method: "PUT", credentials: "include" });
      if (res.ok) { toast.success("Flags dismissed"); onRefresh?.(); }
    } catch {}
  };

  const handleVote = async (postId, optionIndex) => {
    try {
      const res = await fetch(`${API}/feed/posts/${postId}/vote`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ option_index: optionIndex }),
      });
      if (res.ok) onRefresh?.();
    } catch {}
  };

  const toggleComments = () => {
    if (!showComments) fetchComments();
    setShowComments(!showComments);
  };

  const isOwner = post.author_id === user?.user_id;
  const flagCount = post.flags?.length || 0;

  return (
    <Card className={`card-light overflow-hidden ${post.is_pinned ? 'border-[#FFD507]/40 ring-1 ring-[#FFD507]/20' : ''}`} data-testid={`feed-post-${post.post_id}`}>
      {post.is_pinned && (
        <div className="bg-[#FFD507]/10 px-4 py-1.5 flex items-center gap-1.5 border-b border-[#FFD507]/20">
          <Pin className="w-3 h-3 text-[#1E3A8A]" />
          <span className="text-[11px] font-semibold text-[#1E3A8A]">Pinned Post</span>
        </div>
      )}
      
      {/* Admin flag banner */}
      {isAdmin && flagCount > 0 && (
        <div className="bg-red-50 px-4 py-1.5 flex items-center justify-between border-b border-red-100">
          <span className="text-[11px] text-red-600 font-medium"><Flag className="w-3 h-3 inline mr-1" />{flagCount} flag{flagCount > 1 ? 's' : ''}</span>
          <button onClick={handleDismissFlags} className="text-[11px] text-red-500 underline">Dismiss</button>
        </div>
      )}

      <CardContent className="py-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#0F1B3D] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {post.author_name?.charAt(0) || "?"}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-slate-800">{post.author_name}</span>
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${CATEGORY_COLORS[post.category] || 'bg-slate-100 text-slate-600'}`}>
                  {CATEGORIES.find(c => c.value === post.category)?.label || post.category}
                </Badge>
              </div>
              <p className="text-[11px] text-slate-400">
                {ROLE_LABELS[post.author_role] || post.author_role} · {new Date(post.created_at).toLocaleDateString()} at {new Date(post.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
              </p>
            </div>
          </div>
          
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`post-menu-${post.post_id}`}>
              <MoreHorizontal className="w-4 h-4 text-slate-400" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-8 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-10 min-w-[140px]">
                {(isOwner || isAdmin) && (
                  <button onClick={() => { handleDelete(); setShowMenu(false); }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                )}
                {isAdmin && (
                  <button onClick={() => { handlePin(); setShowMenu(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                    <Pin className="w-3.5 h-3.5" /> {post.is_pinned ? "Unpin" : "Pin"}
                  </button>
                )}
                {!isOwner && (
                  <button onClick={() => { handleFlag(); setShowMenu(false); }} className="w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2">
                    <Flag className="w-3.5 h-3.5" /> Report
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {post.content && <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{post.content}</p>}

        {/* Media */}
        <MediaDisplay media={post.media} />

        {/* Poll */}
        {post.poll && <PollDisplay poll={post.poll} postId={post.post_id} onVote={handleVote} />}

        {/* Actions */}
        <div className="flex items-center gap-1 mt-4 pt-3 border-t border-slate-100">
          <button onClick={handleLike} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors text-sm ${liked ? 'text-red-500 bg-red-50' : 'text-slate-500 hover:bg-slate-50'}`} data-testid={`like-btn-${post.post_id}`}>
            <Heart className={`w-4 h-4 ${liked ? 'fill-red-500' : ''}`} />
            <span className="text-xs font-medium">{likeCount || ""}</span>
          </button>
          <button onClick={toggleComments} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors text-sm ${showComments ? 'text-[#1E3A8A] bg-blue-50' : 'text-slate-500 hover:bg-slate-50'}`} data-testid={`comment-toggle-${post.post_id}`}>
            <MessageCircle className="w-4 h-4" />
            <span className="text-xs font-medium">{post.comment_count || ""}</span>
          </button>
        </div>

        {/* Comments */}
        {showComments && (
          <CommentSection postId={post.post_id} comments={comments} onCommentAdded={fetchComments} onCommentDeleted={fetchComments} />
        )}
      </CardContent>
    </Card>
  );
};

// ============ MAIN FEED PAGE ============
const CommunityFeed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/feed/posts?page=${page}&limit=15`;
      if (category !== "all") url += `&category=${category}`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts);
        setTotalPages(data.pages);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [category, page]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  return (
    <LightPageLayout title="Community Feed" subtitle="Connect, share, and grow with the Aspiritech Academy community.">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="community-feed-page">
        {/* Post Composer */}
        <PostComposer onPostCreated={() => { setPage(1); fetchPosts(); }} />

        {/* Category Tabs */}
        <Tabs value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <TabsTrigger key={cat.value} value={cat.value} className="data-[state=active]:bg-[#0F1B3D] data-[state=active]:text-white text-xs gap-1.5 px-3" data-testid={`feed-tab-${cat.value}`}>
                  <Icon className="w-3.5 h-3.5" /> {cat.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* Posts */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
            <p className="text-sm text-slate-400 mt-2">Loading feed...</p>
          </div>
        ) : posts.length === 0 ? (
          <Card className="card-light">
            <CardContent className="py-12 text-center">
              <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">No posts yet in this category</p>
              <p className="text-sm text-slate-400 mt-1">Be the first to share something!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map(post => (
              <FeedPostCard key={post.post_id} post={post} onRefresh={fetchPosts} isAdmin={isAdmin} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs">
              <ChevronUp className="w-3.5 h-3.5 mr-1 rotate-[-90deg]" /> Newer
            </Button>
            <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs">
              Older <ChevronDown className="w-3.5 h-3.5 ml-1 rotate-[-90deg]" />
            </Button>
          </div>
        )}
      </div>
    </LightPageLayout>
  );
};

export default CommunityFeed;
